import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { HmsPush } from '@/services/hms-push.plugin';

const ADMIN_AUTHORITIES = ['ROLE_ADMIN', 'ROLE_MANAGER'];

/** Listeners attach once per app lifecycle; each admin login re-runs registration to re-bind the token. */
let fcmListenersAttached = false;

function hasAdminAuthority(authorities?: unknown): boolean {
  if (!Array.isArray(authorities)) return false;
  return authorities.some((authority) => ADMIN_AUTHORITIES.includes(String(authority)));
}

async function sendToken(provider: 'FCM' | 'HMS', token: string): Promise<void> {
  const { value: jwt } = await Preferences.get({ key: 'jwt' });
  if (!jwt) return;
  await CapacitorHttp.post({
    url: `${process.env.VUE_APP_API_URL}api/account/admin-push-device`,
    headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
    data: { provider, token },
    readTimeout: 15000,
  });
}

async function onFcmRegistration(token: Token): Promise<void> {
  if (!token.value) return;
  await sendToken('FCM', token.value);
}

async function registerFcmPush(): Promise<void> {
  if (!fcmListenersAttached) {
    fcmListenersAttached = true;
    await PushNotifications.addListener('registration', onFcmRegistration);
    await PushNotifications.addListener('registrationError', (error) => {
      console.warn('FCM push registration failed', error);
    });
  }

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') {
    return;
  }
  await PushNotifications.register();
}

async function registerHmsPush(): Promise<void> {
  try {
    const { token } = await HmsPush.register();
    if (token) {
      await sendToken('HMS', token);
    }
  } catch (error) {
    console.warn('HMS push registration failed', error);
  }
}

export async function registerAdminPushNotifications(authorities?: unknown): Promise<void> {
  if (!Capacitor.isNativePlatform() || !hasAdminAuthority(authorities)) return;

  if (Capacitor.getPlatform() === 'ios') {
    await registerFcmPush();
    return;
  }

  if (Capacitor.getPlatform() === 'android') {
    const { provider } = await HmsPush.getPushProvider();
    if (provider === 'HMS') {
      await registerHmsPush();
    } else {
      await registerFcmPush();
    }
  }
}
